/**
 * String Formatter Utility
 * Provides string formatting and cleaning functions
 */

/**
 * Cleans an address string by removing redundant commas and empty segments
 * Examples:
 *   ", , , , VN" -> "VN"
 *   "111, , , Tỉnh Cao Bằng, VN" -> "111, Tỉnh Cao Bằng, VN"
 *   "111 Nguyen Dinh Chinh,,, Thàn..." -> "111 Nguyen Dinh Chinh, Thàn..."
 *
 * @param address - The address string to clean
 * @returns Cleaned address string with no redundant commas
 */
export function cleanAddressString(address: string | null | undefined): string {
  if (!address) return '';

  // Split by comma, trim each segment, and filter out empty strings
  const segments = address
    .split(',')
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);

  // Join with single comma and space
  return segments.join(', ');
}

/**
 * Truncates a file name while preserving the file extension
 * Only truncates if the text would exceed the container width
 * This function is used as a fallback - CSS should handle most truncation
 * 
 * @param fileName - The file name to truncate
 * @param containerWidth - Width of the container in pixels (optional)
 * @param fontSize - Font size in pixels (default: 14)
 * @returns File name (truncated only if necessary), preserving extension
 */
export function truncateFileName(
  fileName: string | null | undefined,
  containerWidth?: number,
  fontSize: number = 14,
): string {
  if (!fileName) return '';

  // If no container width provided, return as is (CSS will handle truncation)
  if (!containerWidth) return fileName;

  // Estimate character width (average character is about 0.6 * font size)
  const avgCharWidth = fontSize * 0.6;
  
  // Calculate 80% of container width (4/5)
  const maxWidth = containerWidth * 0.8;
  
  // Estimate how many characters fit in 80% of container
  const maxChars = Math.floor(maxWidth / avgCharWidth);
  
  // If file name fits, return as is
  if (fileName.length <= maxChars) return fileName;

  // Truncate and add ellipsis
  return fileName.substring(0, maxChars - 3) + '...';
}